import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import enTranslations from '../translations/en.json';
import deTranslations from '../translations/de.json';
import frTranslations from '../translations/fr.json';
import itTranslations from '../translations/it.json';

const LanguageContext = createContext();

const translations = {
  en: enTranslations,
  de: deTranslations,
  fr: frTranslations,
  it: itTranslations
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get language from localStorage or default to 'de' (German)
    if (typeof window !== 'undefined') {
      const savedLanguage = localStorage.getItem('language');
      if (savedLanguage && ['en', 'de', 'fr', 'it'].includes(savedLanguage)) {
        return savedLanguage;
      }
    }
    return 'de';
  });

  useEffect(() => {
    // Save language preference to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('language', language);
    }
  }, [language]);

  const changeLanguage = useCallback((lang) => {
    if (['en', 'de', 'fr', 'it'].includes(lang)) {
      setLanguage(lang);
    } else {
    }
  }, []);

  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        // Fallback to English if translation not found
        value = translations.en;
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object') {
            value = value[fallbackKey];
          } else {
            return key; // Return key if translation not found
          }
        }
        break;
      }
    }
    
    return value || key;
  }, [language]);

  // Create new context value object when language changes to ensure re-renders
  // Don't memoize t in the dependency - recreate the object each time language changes
  const contextValue = useMemo(() => ({
    language,
    changeLanguage,
    t: (key) => {
      const keys = key.split('.');
      let value = translations[language];
      
      for (const k of keys) {
        if (value && typeof value === 'object') {
          value = value[k];
        } else {
          value = translations.en;
          for (const fallbackKey of keys) {
            if (value && typeof value === 'object') {
              value = value[fallbackKey];
            } else {
              return key;
            }
          }
          break;
        }
      }
      
      return value || key;
    }
  }), [language, changeLanguage]);

  // Debug: log when language changes
  useEffect(() => {
  }, [language]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

